<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import AppTile from '../molecules/app/AppTile.vue';
import TextHeadline from '../atoms/app/text/TextHeadline.vue';
import AppLink from '../atoms/app/AppLink.vue';
import AppIcon from '../atoms/app/AppIcon.vue';
import { useJobDataStore } from '../../stores/job-store';
import { getHumanizedDateDifference } from '../../utils/date';
import CompanyLogoBox from './CompanyLogoBox.vue';
import { usePayloadStore } from '../../stores/payload-store';
import { useBreakpoints } from '../../composables/breakpoint';
import KeyFactBoxListItem from '../molecules/KeyFactBoxListItem.vue';
import WebOnly from '../webview/WebOnly.vue';
import TextBase from "../atoms/app/text/TextBase.vue";

const props = defineProps({
    previewMode: {
        type: Boolean,
        default: false,
    },
});

const jobData = useJobDataStore();
const { company } = storeToRefs(jobData);

// Create computed refs of branding properties for a cleaner usage
const hasBrandingHeaderImage = computed(() => Boolean(company.value.branding?.headerImage));
const isBrandable = computed(() => Boolean(company.value.branding?.isBrandable));
const hasBrandColor = computed(() => Boolean(company.value.branding?.color));

const payload = usePayloadStore();

const showCreatedAtTimeString = computed(() => Boolean(jobData.jobId));
const createdAtTimeString = computed(() => getHumanizedDateDifference(jobData.date));

// Widget jobs don't have a jobId, in that case we need to hide some fields that would be empty
const showJobInfos = computed(() => payload.jobId);

const showCompanyInfos = computed(() => company.value.employees || company.value.locations);

const { isMobile } = useBreakpoints();
const isExpanded = ref(false);
const expandable = computed(() => isMobile.value && payload.jobId !== 0);
watch(() => props.previewMode, () => {
    isExpanded.value = false;
});
</script>

<template>
    <AppTile
        class="sticky top-l flex flex-col overflow-hidden rounded-lg border border-solid splitview:mb-5xl"
        :class="{
            'pt-xl splitview:pt-3xl': hasBrandingHeaderImage,
            'h-[20rem]': expandable && !isExpanded,
        }"
    >
        <div
            class="absolute w-full left-0 top-0 bg-cover"
            :class="hasBrandingHeaderImage
                ? 'h-[5rem] bg-brandingImage bg-cover after:bg-gradient-to-t after:from-white after:w-full '
                    + 'after:h-3xl after:absolute after:bottom-0 after:left-0 splitview:after:hidden'
                : 'h-4xl bg-brandingDefaultImage bg-[length:100%_100%]'"
            :data-qa="hasBrandingHeaderImage ? 'branding header' : 'default header'"
        />

        <div
            v-if="isBrandable && hasBrandColor"
            class="absolute h-[3px] w-full left-0 top-0 bg-branding"
            data-qa="branding bar"
        />

        <span
            v-if="showCreatedAtTimeString"
            class="splitview:hidden absolute z-10 bg-gray-50 inline w-auto flex-grow-0
                               rounded-full pl-s pr-s text-gray-900 self-end"
        >
            {{ createdAtTimeString }}
        </span>
        <div
            class="flex flex-col gap-xl"
        >
            <!-- Company key facts -->
            <div class="flex flex-col gap-l splitview:gap-xl splitview:items-center splitview:text-center">
                <div class="k-o-media__figure z-10">
                    <CompanyLogoBox class="w-4xl h-4xl"/>
                </div>
                <div class="content-center flex flex-col items-start splitview:items-center gap-2">
                    <div>
                        <TextHeadline
                            variant="lg-400"
                            data-qa="job title"
                        >
                            {{ jobData.title }}
                        </TextHeadline>
                        <TextBase
                            class="mt-xs text-gray-600"
                            data-qa="company name"
                        >
                            {{ jobData.companyName }}
                        </TextBase>
                    </div>
                    <span
                        class="hidden splitview:inline px-3 py-[4px] rounded-full bg-gray-50"
                    >
                        {{ createdAtTimeString }}
                    </span>
                </div>
            </div>

            <hr
                class="border-solid border-t h-0"
                :class="isBrandable && hasBrandColor ? 'border-branding' : 'border-gray-100'"
            >
            <div class="flex flex-col splitview:flex-row justify-between gap-l items-start">
                <!-- Job Overview -->
                <div
                    v-if="showJobInfos"
                    class="flex flex-col gap-m"
                >
                    <TextBase variant="base-500">
                        Über den Job
                    </TextBase>
                    <ul class="flex flex-col gap-s">
                        <KeyFactBoxListItem
                            icon="clock"
                            :label="jobData.employmentTypeLabel"
                        />
                        <KeyFactBoxListItem
                            icon="euro"
                            :label="jobData.salaryLabel"
                        />
                        <KeyFactBoxListItem
                            icon="briefcase-heart"
                            :label="jobData.jobLevelLabel"
                        />
                        <KeyFactBoxListItem
                            v-if="jobData.homeOffice"
                            icon="house"
                            label="Homeoffice möglich"
                        />
                        <KeyFactBoxListItem
                            data-qa="job location icon"
                            icon="map-pin"
                            :label="jobData.locations"
                        />
                    </ul>
                </div>

                <WebOnly>
                    <AppLink
                        v-if="jobData.link"
                        :href="jobData.link"
                        target="_blank"
                        rel="noopener"
                        class="flex flex-shrink-0 h-full"
                        :class="{
                            'k-c-link--mixed hover:text-gray-900': isBrandable && hasBrandColor,
                            link: !hasBrandColor,
                        }"
                        data-gtm-element="AP_F: Job"
                        data-gtm-element-detail="Detail Click"
                    >
                        <AppIcon
                            class="mt-[1px] mr-s"
                            name="external-link"
                            size="xl"
                        />
                        Zum Inserat
                    </AppLink>
                </WebOnly>
            </div>

            <!-- Company Overview -->
            <div
                v-if="showCompanyInfos"
                class="pt-xl border-solid border-t"
                :class="isBrandable && hasBrandColor ? 'border-branding' : 'border-gray-100'"
            >
                <TextBase variant="base-500">
                    Über das Unternehmen
                </TextBase>
                <ul class="mt-m flex flex-col gap-s">
                    <KeyFactBoxListItem
                        icon="person-group"
                        :label="company.employees"
                    />
                    <KeyFactBoxListItem
                        data-qa="company location icon"
                        icon="map-pin"
                        :label="company.mainLocation"
                    />
                </ul>
            </div>
            <hr
                v-if="expandable"
                class="border-solid border-t h-0"
                :class="isBrandable && hasBrandColor ? 'border-branding' : 'border-gray-100'"
            >
            <template v-if="expandable">
                <div
                    class="w-full bg-gradient-to-t from-white from-40%"
                    :class="{
                        'absolute bottom-0 left-0 h-[9rem]': !isExpanded,
                        relative: isExpanded,
                    }"
                >
                    <AppLink
                        tag="button"
                        type="button"
                        data-qa="application summary toggle"
                        :class="{
                            'absolute bottom-xl left-xl': !isExpanded,
                            'k-c-link--mixed hover:text-gray-900': isBrandable && hasBrandColor,
                        }"
                        @click="isExpanded = !isExpanded"
                    >
                        <template v-if="isExpanded">
                            Weniger anzeigen
                        </template>
                        <template v-else>
                            Mehr anzeigen
                        </template>
                        <AppIcon
                            name="arrow-down"
                            class="ml-s transition-transform"
                            :class="isExpanded && 'rotate-180'"
                        />
                    </AppLink>
                </div>
            </template>
        </div>
    </AppTile>
</template>

<style lang="scss">
@use '~@karriereat/global-styles/scss/settings/spacing';
@use '~@karriereat/global-styles/scss/components/image-box' as imageBox;

.bg-brandingDefaultImage {
    background: linear-gradient(45deg, var(--k-yellow-100), var(--k-green-100));
}
</style>
